import { EventEmitter, Injectable, Input, Output } from '@angular/core';
import { API_ROUTES } from '../api/api-routes';
import { GetPageContent, IsReadStatusUpdateContent, Notification, NotificationMessageType, NotificationResponseType, NotificationWSMessage, NotificationWSResponse } from '../../core/interfaces/notifications-interface';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private socket: WebSocket | null = null;

  private reconnectInterval: any;
  private heartbeatInterval: any;

  private heartbeatIntervalTime: number = 120000;
  private reconnectIntervalTime: number = 10000;

  // Notifications
  private listOfNotifications: Notification[] = [];
  private currentPage = 1;
  private perPage = 20;

  private waitingForNewPage = false;
  private requestedPageNumber = 1;

  private unreadCount: number = 0;

  constructor() { }

  connect(): void {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      return;
    }

    this.socket = new WebSocket(API_ROUTES.NOTIFICATIONS.WS);

    // WebSocket connected
    this.socket.onopen = () => {
      this.listOfNotifications = [];

      // Sending ping to keep connection open
      this.heartbeatInterval = setInterval(() => {
        if (this.socket?.readyState === WebSocket.OPEN) {
          this.socket.send(JSON.stringify({ type: 'ping' }));
        }
      }, this.heartbeatIntervalTime);
    };

    // Notification received
    this.socket.onmessage = (event) => {
      const data = JSON.parse(event.data) as NotificationWSResponse;

      if (data.type === NotificationResponseType.INITIAL) {
        this.listOfNotifications = data.content as Notification[];
        this.unreadCount = data.unread_notifications ?? 0;
      } else if (data.type === NotificationResponseType.MESSAGE) {
        this.listOfNotifications = [data.content as Notification, ...this.listOfNotifications]
        this.unreadCount = data.unread_notifications ?? 0;
      } else if (data.type === NotificationResponseType.NEXT_PAGE) {
        this.waitingForNewPage = false;
        this.currentPage = this.requestedPageNumber;

        const newNotifications = data.content as Notification[];
        const existingIds = new Set(this.listOfNotifications.map(n => n.id));

        const uniqueNewNotifications = newNotifications.filter(n => !existingIds.has(n.id));

        this.listOfNotifications = [...this.listOfNotifications, ...uniqueNewNotifications];

        this.unreadCount = data.unread_notifications ?? 0;
      }
    };

    // WebSocket disconnected
    this.socket.onclose = () => {
      this.socket = null;
      this.listOfNotifications = [];

      clearInterval(this.heartbeatInterval);

      // Try reconnecting
      this.reconnectInterval = setTimeout(() => {
        this.connect();
      }, this.reconnectIntervalTime);
    };

    this.socket.onerror = (error) => {
      this.socket?.close();
    };
  }

  disconnect(): void {
    if (this.reconnectInterval) clearTimeout(this.reconnectInterval);
    if (this.heartbeatInterval) clearInterval(this.heartbeatInterval);

    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }

    this.listOfNotifications = [];
  }

  getListOfNotifications(): Notification[] {
    return this.listOfNotifications;
  }

  getUnreadNotificationsCount(): number {
    return this.unreadCount;
  }

  sendSetAsRead(id: string, is_read: boolean) {
    if (!this.socket) {
      return;
    }

    var content: IsReadStatusUpdateContent = { id: id, is_read: is_read }
    var message: NotificationWSMessage = { type: NotificationMessageType.IS_READ_STATUS_UPDATE, content: content };
    this.socket.send(JSON.stringify(message));

    this.unreadCount--;
  }

  requestNewPage() {
    if (!this.socket || this.waitingForNewPage) {
      return;
    }

    this.requestedPageNumber = (this.currentPage + 1);
    var content: GetPageContent = { new_page: this.requestedPageNumber, per_page: this.perPage }
    var message: NotificationWSMessage = { type: NotificationMessageType.GET_PAGE, content: content };
    this.socket.send(JSON.stringify(message));
    this.waitingForNewPage = true;
  }

  getWaitingForNextPage() {
    return this.waitingForNewPage;
  }
}
